import { useDiscContextMenu } from '@/modules/imports/container/scan-discs-popup-container/editor/useDiscContextMenu'
import React from 'react'
import DiscViewer from './DiscViewer'
import TachoDiscActivites from './TachoDiscActivites'
import TachoDiscSection from './TachoDiscSection'
import TachoDiscSummary from './TachoDiscSummary'
import TachoDiscFooter from './TachoDiscFooter'
import {
  discToEditorState,
  useChartEditor,
} from '@/modules/imports/container/scan-discs-popup-container/editor/useChartEditor'
import { inDayPieces } from '@/modules/imports/container/scan-discs-popup-container/editor/chartDay'
import {
  ChartScanDisc,
  UpdateDiscConflict,
  UpdateDiscPayload,
  redetectChartDisc,
  useUpdateDisc,
} from '@/lib/api/company/chartScan'
import { ConflictDialog } from '@/modules/imports/container/scan-discs-popup-container/editor/ConflictDialog'
import { useChartT } from '@/modules/imports/container/scan-discs-popup-container/i18n'

const TachoDiscContent = ({
  isLoading,
  disc,
  companyId,
  scanId,
  onSaved,
  onClose,
  discIndex = 0,
  discCount = 1,
  globalIndex,
  globalCount,
  hasMoreAfter = false,
  onPrev,
  onNext,
}: {
  isLoading: boolean
  disc: ChartScanDisc | null
  companyId: string
  scanId: number
  onSaved?: () => void
  onClose?: () => void
  discIndex?: number
  discCount?: number
  globalIndex?: number
  globalCount?: number
  hasMoreAfter?: boolean
  onPrev?: () => void
  onNext?: () => void
}) => {
  if (isLoading || !disc) {
    return (
      <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-overflow-hidden tw-pr-2">
        <div className="tw-text-sm tw-text-slate-400">Wczytywanie tarczki...</div>
      </div>
    )
  }

  return (
    <TachoDiscLoaded
      disc={disc}
      companyId={companyId}
      scanId={scanId}
      onSaved={onSaved}
      onClose={onClose}
      discIndex={discIndex}
      discCount={discCount}
      globalIndex={globalIndex}
      globalCount={globalCount}
      hasMoreAfter={hasMoreAfter}
      onPrev={onPrev}
      onNext={onNext}
    />
  )
}

const TachoDiscLoaded = ({
  disc,
  companyId,
  scanId,
  onSaved,
  onClose,
  discIndex = 0,
  discCount = 1,
  globalIndex,
  globalCount,
  hasMoreAfter = false,
  onPrev,
  onNext,
}: {
  disc: ChartScanDisc
  companyId: string
  scanId: number
  onSaved?: () => void
  onClose?: () => void
  discIndex?: number
  discCount?: number
  globalIndex?: number
  globalCount?: number
  hasMoreAfter?: boolean
  onPrev?: () => void
  onNext?: () => void
}) => {
  const T = useChartT()
  const [openIndex, setOpenIndex] = React.useState<number>(0)
  const editor = useChartEditor(disc)
  const contextMenu = useDiscContextMenu(companyId, disc.discId, editor)
  const updateDisc = useUpdateDisc(companyId, scanId)
  const [isSaving, setIsSaving] = React.useState(false)
  // Overlapping activities reported by the server; the dialog decides whether we
  // retry the save with `force`.
  const [conflicts, setConflicts] = React.useState<UpdateDiscConflict[] | null>(null)

  // Auto-redetect when a disc opens with no activities. Activities can't be
  // persisted without a driver (TachographChartActivityEntity.DriverEntityId is
  // a non-null FK), and the driver is picked *here*, so a disc coming out of the
  // assign step always arrives with an empty list. Re-run the chart reader on
  // load so the detected activities are there to review and save. Skipped when
  // the disc already has activities (don't clobber a save).
  const autoRedetectedDiscRef = React.useRef<number | null>(null)
  React.useEffect(() => {
    if (disc.activities.length > 0) return
    if (autoRedetectedDiscRef.current === disc.discId) return
    autoRedetectedDiscRef.current = disc.discId
    let cancelled = false
    redetectChartDisc(companyId, disc.discId)
      .then((r) => {
        if (cancelled || r.data.length === 0) return
        // Re-seed from this disc + the detected activities rather than
        // `replaceActivities`, which closes over the editor state of this render
        // and mid-navigation can still hold the previous disc's metadata.
        editor.reset({ ...discToEditorState(disc), activities: r.data })
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, disc.discId])
  const toggle = (index: number) =>
    setOpenIndex((current) => (current === index ? -1 : index))

  // WPF lists the day from its start and hides what lies outside it.
  const dayPieces = React.useMemo(
    () => inDayPieces(editor.activities, editor.dayStartDate, editor.dayEndDate),
    [editor.activities, editor.dayStartDate, editor.dayEndDate],
  )

  const buildPayload = (): UpdateDiscPayload => ({
    driverId: editor.driverId,
    vehicleId: editor.vehicleId,
    dayStartDate: editor.dayStartDate,
    dayEndDate: editor.dayEndDate,
    beginCountry: editor.beginCountry,
    endCountry: editor.endCountry,
    noonShiftMinutes: editor.noonShiftMinutes,
    firstVehicleOdometerStart: editor.firstVehicleOdometerStart,
    firstVehicleOdometerEnd: editor.firstVehicleOdometerEnd,
    distanceFromChart: editor.distanceFromChart,
    tachographStatus: editor.tachographStatus,
    isCrewWork: editor.isCrewWork,
    isClosed: editor.isClosed,
    addManualEntryOnDayStart: editor.addManualEntryOnDayStart,
    addManualEntryOnDayEnd: editor.addManualEntryOnDayEnd,
    dayNote: editor.dayNote,
    violations: editor.violations,
    activities: editor.activities.map((a) => ({
      startTime: a.startTime,
      endTime: a.endTime,
      type: a.type,
      manualEntry: a.manualEntry,
      ferryTrainCrossing: a.ferryTrainCrossing,
      crew: a.crew,
    })),
    additionalVehiclesUsed: editor.additionalVehiclesUsed.map((v) => ({
      vehicleId: v.vehicleId,
      startTime: v.startTime,
      endTime: v.endTime,
      odometerStart: v.odometerStart,
      odometerEnd: v.odometerEnd,
      distanceFromChart: v.distanceFromChart,
    })),
    countryChanges: editor.countryChanges.map((c) => ({
      country: c.country,
      previousCountry: c.previousCountry,
      atTime: c.atTime,
    })),
    distanceData: editor.distanceData,
  })

  // Activities are FK'd to a driver, so the server can't persist without one.
  const missingDriver = editor.driverId == null
  // WPF blocks the save without a vehicle too; the server would drop the odometer silently.
  const missingVehicle = editor.vehicleId == null

  const afterSaved = () => {
    onSaved?.()
    if (onNext) onNext()
    else onClose?.()
  }

  // `force` tells the server to skip the overlap check (nothing is deleted — the chart
  // is merged with the driver's other data). Never send it up front: save without it,
  // and only retry with it once the user has seen the overlapping rows and confirmed.
  const persist = async (force: boolean) => {
    if (isSaving || missingDriver || missingVehicle) return
    setIsSaving(true)
    try {
      const resp = await updateDisc.mutateAsync({
        discId: disc.discId,
        payload: { ...buildPayload(), force },
      })
      if (!resp.saved && resp.conflicts?.length) {
        setConflicts(resp.conflicts)
        return
      }
      setConflicts(null)
      if (resp.saved) afterSaved()
    } finally {
      setIsSaving(false)
    }
  }

  const handleSave = () => persist(false)


  const total = globalCount ?? discCount
  const position = (globalIndex ?? discIndex) + 1

  const isLast = !onNext && !hasMoreAfter
  const saveLabel =
    total > 1 ? (isLast ? 'Zapisz i zakończ' : `Dalej ${position}/${total}`) : 'Zapisz'

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {discCount > 1 && (
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-200 tw-px-4 tw-py-2">
          <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-slate-900">
            Tarczka {discIndex + 1} z {discCount}
          </h2>
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={onPrev}
              disabled={!onPrev}
              className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1 tw-text-sm disabled:tw-opacity-40"
            >
              ← Poprzednia
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!onNext}
              className="tw-rounded tw-border tw-border-slate-300 tw-px-3 tw-py-1 tw-text-sm disabled:tw-opacity-40"
            >
              Następna →
            </button>
          </div>
        </div>
      )}
      <div className="tw-flex tw-flex-1 tw-overflow-hidden tw-pr-2">
        <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden  tw-border-t tw-border-base-200">
          <TachoDiscSection
            title="Aktywności"
            isFirst
            isOpen={openIndex === 0}
            onToggle={() => toggle(0)}
          >
            <TachoDiscActivites pieces={dayPieces} editor={editor} companyId={companyId} onContextMenu={contextMenu.open} />
          </TachoDiscSection>

          <TachoDiscSection
            title="Podsumowanie"
            isOpen={openIndex === 1}
            onToggle={() => toggle(1)}
          >
            <TachoDiscSummary editor={editor} companyId={companyId} />
          </TachoDiscSection>
        </div>

        <div className="tw-relative tw-flex tw-h-full tw-w-[50%] tw-flex-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-bg-white">
          <DiscViewer
            companyId={companyId}
            disc={disc}
            editor={editor}
            onContextMenu={contextMenu.open}
            menuInteracting={contextMenu.isInteracting}
            onContextMenuVehicleChange={contextMenu.openVehicleChangeMenu}
            onMoveVehicleChange={contextMenu.moveVehicleChange}
          />
        </div>
      </div>

      <TachoDiscFooter
        onSave={handleSave}
        isSaving={isSaving}
        label={saveLabel}
        disabledReason={
          missingDriver
            ? T('errMissingDriver', 'Wybierz kierowcę.')
            : missingVehicle
              ? T('errMissingVehicle', 'Wybierz pojazd.')
              : null
        }
      />

      {contextMenu.content}

      {conflicts && (
        <ConflictDialog
          conflicts={conflicts}
          onCancel={() => setConflicts(null)}
          onConfirm={() => {
            setConflicts(null)
            persist(true)
          }}
        />
      )}
    </div>
  )
}

export default TachoDiscContent
